/**
 * THE DOOR, WHEN THE PLATFORM HOLDS THE KEY. Phase 26, DESIGN-DELTAS 1ag.1.
 *
 * Under the Jest build there is no Gate: no name, no address, no password, because their launch
 * rules forbid a sign-in of the game's own inside their app. What replaces it is this screen,
 * which asks the SDK for the signed player, hands it to `POST /api/jest/login`, keeps the bearer
 * the server answers with, and boots exactly as any build does. It is on screen for one round
 * trip and it says so: "Opening the door."
 *
 * THE FIFTEEN-SECOND RULE. The platform shows its own overlay until the game reports progress,
 * and exits the player to their home screen if none arrives in fifteen seconds. So the first
 * thing this screen does, before `init`, is report 30. `App` reports 100 once the boot read has
 * answered, and this screen marks the game loaded as soon as `start()` resolves for the same
 * reason: a refusal that leaves the overlay up is a refusal nobody reads.
 *
 * A refusal is the server's sentence, with a Retry. A LOCKED answer is the one case with no
 * retry: the deployment has no Jest game configured yet, and pressing again will not make one.
 */

import { useEffect, useState } from 'react'
import { A } from '../art'
import { ApiError } from '../net/api'
import { Btn } from '../ui/Kit'
import { useSession } from '../game/session'
import { jestInit, jestLoaded, jestProgress, jestSignIn, sdk } from '../jest'

export function JestGate() {
  const start = useSession((s) => s.start)
  const [fault, setFault] = useState<string | null>(null)
  const [locked, setLocked] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let dead = false
    setFault(null)
    setLocked(false)
    const run = async () => {
      // Before anything that can wait: the overlay's clock is already running.
      jestProgress(30)
      if (sdk() === null) {
        throw new Error('The platform did not answer. Open the game from Jest.')
      }
      await jestInit()
      jestProgress(50)
      const r = await jestSignIn()
      if (r === null) throw new Error('Jest sent no player. Open the game again.')
      jestProgress(80)
      await start()
      jestLoaded()
      // `start()` answers a 401 as the signed-out phase rather than an error, which under this
      // build means the bearer the server just minted was not accepted by the next read. That is
      // a fault here, not a state, because there is no form for the player to fill in instead.
      if (!dead && useSession.getState().phase === 'anon') {
        throw new Error('The door did not open. Try again.')
      }
    }
    run().catch((e: unknown) => {
      if (dead) return
      jestLoaded()
      if (e instanceof ApiError && e.code === 'LOCKED') {
        setLocked(true)
        return
      }
      setFault(e instanceof Error ? e.message : 'The door did not open. Try again.')
    })
    return () => {
      dead = true
    }
  }, [attempt, start])

  return (
    <div
      role="main"
      aria-label="Opening the door"
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        padding: 24,
        background: `var(--coal) url(${A('brand.loading_key_art_wide')}) center / cover no-repeat`,
      }}
    >
      <div style={{ textAlign: 'center', maxWidth: 420, textShadow: '0 2px 16px #000' }}>
        <div
          className="t-display"
          style={{ fontSize: 40, letterSpacing: '.1em', color: 'var(--ink)' }}
        >
          ASHPORT
        </div>
        {locked ? (
          <div style={{ color: 'var(--ink-mid)', marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>
            Ashport is not open on Jest yet. Come back soon.
          </div>
        ) : fault !== null ? (
          <>
            <div style={{ color: 'var(--heat)', marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>{fault}</div>
            <div style={{ marginTop: 16 }}>
              <Btn v="gold" onClick={() => setAttempt((n) => n + 1)} id="jest-retry">
                Try again
              </Btn>
            </div>
          </>
        ) : (
          <div
            className="t-label"
            style={{ color: 'var(--ink-mid)', marginTop: 12, fontSize: 11, letterSpacing: '.18em' }}
          >
            Opening the door
          </div>
        )}
      </div>
    </div>
  )
}
